import React, { FC, useCallback, useRef } from 'react';
import { Box } from '@src/atoms/Box';
import { Icon } from '@src/atoms/Icon';
import { Text } from '@src/atoms/Text';
import { Tooltip } from '@src/atoms/Tooltip';
import { IDataTableColumn, IDataTableRequiredView } from '../models';
import { TSortOrder } from '@src/models/api';
import { changeOrderingEvent } from '../store/events/changeOrderingEvent';
import { useUnit } from 'effector-react';
import { classnames } from '@src/utils/classnames';
import { ITooltipProps } from '@src/atoms/Tooltip/Tooltip';
import { IComponentChildrenProps } from '@src/models/props';
import { DATA_ELEM_HEAD_CELL } from '../consts';

import styles from './DataTableHeadCell.module.scss';
import { getClasses } from '@src/utils/getClass';
import { HEAD_CELL_FONT_SIZE_BY_VIEW, HEAD_CELL_LINES_NUMBER_BY_VIEW } from '@src/molecules/DataTable/_HeadCell/consts';
import { TooltipNew } from '@src/atoms/TooltipNew';
import { Checkbox } from '@src/atoms/Checkbox';

interface IDataTableHeadCellProps extends IDataTableRequiredView {
  column: IDataTableColumn;
  sortOrder?: TSortOrder;
}

interface IArrowProp {
  sortOrder: TSortOrder;
  active?: boolean;
}

const Arrow: FC<IArrowProp> = ({ active, sortOrder }) => {
  const rotateDeg = sortOrder === 'DESC' ? 90 : 270;
  return (
    <Icon
      name={'directionRight'}
      size={active ? 16 : 12}
      view={active ? 'basicBlack' : 'basicGreySemiDark'}
      style={{ transform: `rotate(${rotateDeg}deg)` }}
    />
  );
};

const ArrowUpDown: FC = () => {
  return (
    <Box alignItems={'center'}>
      <Arrow sortOrder={'ASC'} />
      <Arrow sortOrder={'DESC'} />
    </Box>
  );
};

interface IWrapperProps extends IComponentChildrenProps {
  tooltip?: ITooltipProps;
}

const Wrapper: FC<IWrapperProps> = ({ children, tooltip }) => {
  if (tooltip) return <Tooltip {...tooltip}>{children}</Tooltip>;

  return <>{children}</>;
};

export const DataTableHeadCell: FC<IDataTableHeadCellProps> = ({ column, sortOrder, view }) => {
  const isSortable = column.sortable;
  const postTooltipContent = column.postTooltipContent;
  const postTooltip = column.postTooltip;
  const checkbox = column.checkbox;
  const headDataAttributes = column.headDataAttributes || {};
  const [changeOrderingEventFn] = useUnit([changeOrderingEvent]);
  const ref = useRef<HTMLDivElement | null>(null);

  const handleClick = useCallback(() => {
    if (isSortable) {
      changeOrderingEventFn({
        columnId: column.id,
      });
    }
  }, [column.id, isSortable, changeOrderingEventFn]);

  const classes = classnames(
    styles.cell,
    styles.root,
    {
      [styles.cellPointer]: isSortable,
    },
    ...getClasses(styles, { view }),
  );

  return (
    <div
      {...headDataAttributes}
      ref={ref}
      className={classes}
      key={column.id}
      onClick={handleClick}
      data-elem={DATA_ELEM_HEAD_CELL}
    >
      <Box alignItems={'center'}>
        <Box alignItems={'center'}>
          <Box>
            {checkbox && <Checkbox id={`${column.title}Checkbox`} onChange={checkbox.cb} value={checkbox.value} />}
          </Box>
          <Wrapper tooltip={column.tooltip}>
            <Text id="title" size={HEAD_CELL_FONT_SIZE_BY_VIEW[view]} noOfLines={HEAD_CELL_LINES_NUMBER_BY_VIEW[view]}>
              {column.title}
            </Text>
          </Wrapper>
        </Box>
        {isSortable && (
          <Box marginLeft={8}>
            {sortOrder ? (
              <>
                <Arrow sortOrder={sortOrder} active={true} />
              </>
            ) : (
              <ArrowUpDown />
            )}
          </Box>
        )}
        {!postTooltip && postTooltipContent && (
          <Box marginLeft={8}>
            <TooltipNew content={postTooltipContent}>
              <Icon id="tooltipIcon" name="circleOutlineQuestion" size={'sm'} />
            </TooltipNew>
          </Box>
        )}
        {!postTooltipContent && postTooltip && (
          <Box marginLeft={8}>
            <TooltipNew content={postTooltip.content}>
              <Icon id="tooltipIcon" name="circleOutlineQuestion" size={'sm'} view={postTooltip.iconView} />
            </TooltipNew>
          </Box>
        )}
      </Box>
    </div>
  );
};
